import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import { BsChevronCompactLeft, BsChevronCompactRight } from "react-icons/bs";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 1;
  flex-grow: 1;
  outline: none !important;
`;

const ContentRowScrollingContent = styled.div`
  display: flex;
  flex-direction: row;
  transition: all ease 0.5s;
`;

const ContentRowWrapper = styled.div`
  position: relative;
  margin-bottom: 5rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 8px;
  border-radius: 20px;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${({ $isActive }) =>
    $isActive ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.5)"};
  transition: background-color 0.3s ease;
`;

const SlideContainer = styled.div`
  width: ${props => props.$slideWidth};
  min-width: ${props => props.$slideWidth};
  height: 80vh;
  position: relative;
  padding-right: 2rem;
  padding-left: 2rem;
  padding-bottom: 10rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 400px;
    background: linear-gradient(to top, #121212, rgba(0, 0, 0, 0));
  }
`;

const SlideTitle = styled.span`
  color: #F0F0F0;
  font-size: 40px;
  font-weight: 900;
  font-family: AnjomanMax;
`;

const SlideTitleEN = styled.span`
  color: #D9D9D9;
  font-size: 36px;
  font-weight: 800;
  font-family: sans-serif;
`;

const TitleDivider = styled.div`
  width: 2px;
  height: 30px;
  background-color: #F5F5F5;
  margin: 0 1rem;
`;

const WatchButton = styled.button`
  width: 200px;
  background-color: #6A0000;
  font-size: 32px;
  font-weight: 900;
  line-height: 45px;
  color: #F0F0F0;
  border-radius: 47px;
  padding: 14px 12px 14px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  margin-top: 1rem;
  transition: background-color 0.2s;
  &:hover {
    background-color: rgb(146, 0, 0);
  }
`;

const NavButton = styled.button`
  background-color: rgba(255, 255, 255, 0.3);
  padding: 16px;
  border-radius: ${props => props.$side === 'left' ? '0 10px 10px 0' : '10px 0 0 10px'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Pagination = ({ activeIndex, length, onDotClick }) => {
  return (
    <PaginationContainer>
      {Array.from({ length }, (_, index) => (
        <Dot
          onClick={() => onDotClick(index)}
          key={index}
          $isActive={index === activeIndex}
        />
      ))}
    </PaginationContainer>
  );
};

const MovieItem = ({ item = {}, onFocus, fixFrame = false, isFirst }) => {
  const navigate = useNavigate();
  const { slug, contentPhotos, title, titleEN, kind, contentType } = item || {};
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(`/${contentType?.kind || kind}/${slug}`);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized slider image with LQIP and WebP support
  const sliderImageData = getOptimizedImageForType("sliderCover", contentPhotos, "TV", { raw: true });
  const videoLogoData = getOptimizedImageForType("videoLogo", contentPhotos, "TV", { raw: true });

  // Sidebar width is 360px, so slide width = 100vw - 360px
  const SIDEBAR_WIDTH = 250;
  const slideWidth = `calc(100vw - ${SIDEBAR_WIDTH}px)`;



  if (fixFrame) {
    return (
      <div ref={ref} className="cursor-pointer" onMouseEnter={handleMouseEnter} onClick={() => navigate(`/${contentType?.kind || kind}/${slug}`)}>
        <SlideContainer $slideWidth={slideWidth}>
          <div className="absolute top-0 left-0 right-0 bottom-0 z-0">
            <img
              src={"/fix_frame_iran.png"}
              className="w-full h-[80vh] object-cover object-left"
              alt={title}
            />
          </div>
        </SlideContainer>
      </div>
    );
  } else {
    return (
      <div
        ref={ref}
        onClick={() => navigate(`/${contentType?.kind || kind}/${slug}`)}
        onMouseEnter={handleMouseEnter}
        className="cursor-pointer"
      >
        <SlideContainer $slideWidth={slideWidth}>
          <div className="absolute top-0 left-0 right-0 bottom-0 z-0">
            <img
              src={sliderImageData.src}
              className="w-full h-[80vh]"
              style={{ objectFit: "cover", objectPosition: "left" }}
              alt={title}
            />
          </div>
          <div style={{ position: "relative", zIndex: 1 }} className="w-full">
            {item.tags?.[0] && item[item.tags?.[0]] === "TURKISH_NATIVE" && (
              <img
                src="/tags/turkish-alt.svg"
                alt={item?.tags?.[0]}
                width={250}
              />
            )}
            <div className="flex justify-between w-full items-center">
              <div className="flex-auto">
                <div className="flex items-center mt-5">
                  <SlideTitle>{title}</SlideTitle>
                  <TitleDivider />
                  <SlideTitleEN>{titleEN}</SlideTitleEN>
                </div>
                <WatchButton $focused={focused}>
                  <FaPlayCircle
                    className="ml-2"
                    style={{ color: "#F0F0F0", fontSize: "24px" }}
                  />
                  تماشا کن
                </WatchButton>
              </div>
              {videoLogoData.src && (
                <div className="min-w-[400px] mt-3">
                  <OptimizedImage
                    alt={title}
                    src={videoLogoData.src}
                    style={{ width: 365 }}
                  />
                </div>
              )}
            </div>
          </div>
        </SlideContainer>
      </div>
    );
  }
};

const AUTOPLAY_INTERVAL_MS = 6000;
const PAUSE_DURATION_MS = 5000;

export const TopSlider = ({ movies, onFocus, id, index }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const { ref, focusKey } = useFocusable({
    onFocus,
    autoRestoreFocus: true,
    focusKey: "slider_" + id,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  // --- Autoplay refs ---
  const autoplayRef = useRef(null);     // setInterval id
  const pauseTimerRef = useRef(null);   // setTimeout id for resuming after interaction
  const isVisibleRef = useRef(true);    // IntersectionObserver visibility flag
  const isPausedRef = useRef(false);    // Whether autoplay is interaction-paused
  const activeIndexRef = useRef(activeIndex); // Keep a ref in-sync for interval callback

  const totalSlides = movies?.length || 0;

  // Keep the ref in sync with state
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // --- Autoplay helpers ---
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (totalSlides <= 1) return; // nothing to cycle
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalSlides);
    }, AUTOPLAY_INTERVAL_MS);
  }, [totalSlides]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  const pauseAutoplay = useCallback(() => {
    stopAutoplay();
    isPausedRef.current = true;
    // Clear any previous resume timer
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      isPausedRef.current = false;
      if (isVisibleRef.current) {
        startAutoplay();
      }
    }, PAUSE_DURATION_MS);
  }, [stopAutoplay, startAutoplay]);

  // --- IntersectionObserver: stop autoplay when offscreen ---
  useEffect(() => {
    const node = ref?.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          // Only restart if not interaction-paused
          if (!isPausedRef.current) startAutoplay();
        } else {
          stopAutoplay();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, startAutoplay, stopAutoplay]);

  // --- Initial autoplay start & full cleanup ---
  useEffect(() => {
    if (totalSlides > 1) startAutoplay();
    return () => {
      stopAutoplay();
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [totalSlides, startAutoplay, stopAutoplay]);

  // --- Slide transform: translate based on activeIndex ---
  const SIDEBAR_WIDTH = 250;
  useEffect(() => {
    if (!innerRef.current) return;
    // Each slide is (100vw - SIDEBAR_WIDTH). Calculate px offset.
    const slideWidthPx = window.innerWidth - SIDEBAR_WIDTH;
    innerRef.current.style.transform = `translateX(${activeIndex * slideWidthPx}px)`;
  }, [activeIndex]);

  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      pauseAutoplay();
      if (isMouseFocus) return; // Skip horizontal scroll on mouse hover
      const { x } = props;
      if (x < 0) {
        innerRef.current.style.transform = `translateX(${Math.abs(x)}px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, isMouseFocus, pauseAutoplay]
  );

  // --- Navigation handlers (pause autoplay on interaction) ---
  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1 < totalSlides ? prev + 1 : prev));
    pauseAutoplay();
  }, [totalSlides, pauseAutoplay]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 >= 0 ? prev - 1 : prev));
    pauseAutoplay();
  }, [pauseAutoplay]);

  const goToSlide = useCallback((idx) => {
    setActiveIndex(idx);
    pauseAutoplay();
  }, [pauseAutoplay]);

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} tabIndex={index}>
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent ref={innerRef}>
            {movies?.map((item, idx) => (
              <MovieItem
                item={item?.content}
                key={"ts_" + item.content?.slug + idx}
                onFocus={(e) => {
                  onAssetFocus(e);
                  setActiveIndex(idx);
                }}
                isFirst={idx === 0}
              />
            ))}
          </ContentRowScrollingContent>
          <div className="absolute left-0 bottom-10 top-0 w-10 flex justify-center items-center">
            <NavButton $side="left" onClick={goNext}>
              <BsChevronCompactLeft
                className="cursor-pointer ml-2"
                color="gray"
                fontSize={48}
              />
            </NavButton>
          </div>
          <div className="absolute right-0 bottom-10 top-0 w-10 flex justify-center items-center">
            <NavButton $side="right" onClick={goPrev}>
              <BsChevronCompactRight
                className="cursor-pointer mr-2"
                color="gray"
                fontSize={48}
              />
            </NavButton>
          </div>
          <div className="absolute left-0 right-0 bottom-10 h-10 flex justify-center items-center">
            <Pagination
              onDotClick={goToSlide}
              activeIndex={activeIndex}
              length={totalSlides}
            />
          </div>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
